import { Building, Floor, Apartment } from '../types/building';
import { supabase } from './supabase';
import { addAuditLog } from './auditLog';
import { encryptData, decryptData } from './encryption';

// Storage keys
const OFFLINE_BUILDINGS_KEY = 'offline_buildings';
const OFFLINE_CHANGES_KEY = 'offline_changes';
const OFFLINE_MODE_KEY = 'offline_mode_enabled';
const SYNC_STATUS_KEY = 'sync_status';

// Types for offline changes
type ChangeType = 'create' | 'update' | 'delete';
type EntityType = 'building' | 'floor' | 'apartment';

interface OfflineChange {
  id: string;
  timestamp: number;
  type: ChangeType;
  entityType: EntityType;
  entityId: string;
  data: any;
  synced: boolean;
  syncAttempts: number;
  lastSyncAttempt?: number;
  error?: string;
}

interface SyncStatus {
  lastSync: number;
  pendingChanges: number;
  failedChanges: number;
  lastError?: string;
}

/**
 * Prüft, ob der Offline-Modus aktiviert ist
 */
export const isOfflineModeEnabled = (): boolean => {
  try {
    const value = localStorage.getItem(OFFLINE_MODE_KEY);
    return value === 'true';
  } catch (error) {
    console.error('Fehler beim Prüfen des Offline-Modus:', error);
    return false;
  }
};

/**
 * Aktiviert oder deaktiviert den Offline-Modus
 */
export const setOfflineMode = (enabled: boolean): void => {
  try {
    localStorage.setItem(OFFLINE_MODE_KEY, enabled.toString());
    
    // Log the mode change
    addAuditLog(
      'update', 
      'system', 
      `Offline mode ${enabled ? 'enabled' : 'disabled'}`
    );
  } catch (error) {
    console.error('Fehler beim Setzen des Offline-Modus:', error);
    throw error;
  }
};

/**
 * Lädt den aktuellen Synchronisierungsstatus
 */
export const getSyncStatus = (): SyncStatus => {
  try {
    const statusJson = localStorage.getItem(SYNC_STATUS_KEY);
    if (!statusJson) {
      return {
        lastSync: 0,
        pendingChanges: getPendingChangesCount(),
        failedChanges: getFailedChangesCount()
      };
    }
    return JSON.parse(statusJson);
  } catch (error) {
    console.error('Fehler beim Laden des Sync-Status:', error);
    return {
      lastSync: 0,
      pendingChanges: 0,
      failedChanges: 0
    };
  }
};

/**
 * Aktualisiert den Synchronisierungsstatus
 */
export const updateSyncStatus = (updates: Partial<SyncStatus>): void => {
  try {
    const currentStatus = getSyncStatus();
    const newStatus = {
      ...currentStatus,
      ...updates
    };
    localStorage.setItem(SYNC_STATUS_KEY, JSON.stringify(newStatus));
  } catch (error) {
    console.error('Fehler beim Aktualisieren des Sync-Status:', error);
  }
};

/**
 * Zählt die ausstehenden Änderungen
 */
export const getPendingChangesCount = (): number => {
  try {
    const changes = loadOfflineChanges();
    return changes.filter(change => !change.synced).length;
  } catch (error) {
    console.error('Fehler beim Zählen der ausstehenden Änderungen:', error);
    return 0;
  }
};

/**
 * Zählt die fehlgeschlagenen Änderungen
 */
export const getFailedChangesCount = (): number => {
  try {
    const changes = loadOfflineChanges();
    return changes.filter(change => !change.synced && change.syncAttempts > 0).length;
  } catch (error) {
    console.error('Fehler beim Zählen der fehlgeschlagenen Änderungen:', error);
    return 0;
  }
};

/**
 * Lädt alle Offline-Änderungen
 */
export const loadOfflineChanges = (): OfflineChange[] => {
  try {
    const encryptedData = localStorage.getItem(OFFLINE_CHANGES_KEY);
    if (!encryptedData) return [];
    
    const decryptedData = decryptData(encryptedData);
    return JSON.parse(decryptedData);
  } catch (error) {
    console.error('Fehler beim Laden der Offline-Änderungen:', error);
    return [];
  }
};

/**
 * Speichert eine Änderung im Offline-Änderungsprotokoll
 */
export const saveOfflineChange = (
  type: ChangeType,
  entityType: EntityType,
  entityId: string,
  data: any
): void => {
  try {
    const changes = loadOfflineChanges();
    
    const newChange: OfflineChange = {
      id: `change-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
      type,
      entityType,
      entityId,
      data,
      synced: false,
      syncAttempts: 0
    };
    
    changes.push(newChange);
    
    // Encrypt changes before storing
    const encryptedData = encryptData(JSON.stringify(changes));
    localStorage.setItem(OFFLINE_CHANGES_KEY, encryptedData);
    
    // Update sync status
    updateSyncStatus({
      pendingChanges: getPendingChangesCount()
    });
    
    // Log the change
    addAuditLog('create', 'system', `Offline change created: ${type} ${entityType} ${entityId}`);
  } catch (error) {
    console.error('Fehler beim Speichern der Offline-Änderung:', error);
    throw error;
  }
};

/**
 * Lädt alle Gebäude aus dem lokalen Speicher
 */
export const loadOfflineBuildings = (): Building[] => {
  try {
    const buildingsJson = localStorage.getItem(OFFLINE_BUILDINGS_KEY);
    if (!buildingsJson) return [];
    
    return JSON.parse(buildingsJson, (key, value) => {
      if (key === 'lastUpdated' && value) {
        return new Date(value);
      }
      return value;
    });
  } catch (error) {
    console.error('Fehler beim Laden der Offline-Gebäude:', error);
    return [];
  }
};

/**
 * Speichert alle Gebäude im lokalen Speicher
 */
export const saveOfflineBuildings = (buildings: Building[]): void => {
  try {
    localStorage.setItem(OFFLINE_BUILDINGS_KEY, JSON.stringify(buildings));
  } catch (error) {
    console.error('Fehler beim Speichern der Offline-Gebäude:', error);
    throw error;
  }
};

/**
 * Lädt Gebäude aus der Datenbank und speichert sie lokal
 */
export const fetchAndCacheBuildings = async (): Promise<Building[]> => {
  try {
    // Lade Gebäude aus der Datenbank
    const { data: buildingsData, error: buildingsError } = await supabase
      .from('buildings')
      .select(`
        id,
        name,
        address,
        organization_id,
        created_at,
        updated_at,
        created_by,
        archived_at,
        metadata,
        floors (
          id,
          number,
          custom_fields,
          apartments (
            id,
            number,
            formatted_number,
            status,
            notes,
            area_type,
            abort_reason,
            technician,
            konzern_id,
            last_updated,
            custom_fields,
            history
          )
        )
      `)
      .order('name');
      
    if (buildingsError) {
      throw buildingsError;
    }
    
    // Transformiere die Daten in das richtige Format
    const buildings: Building[] = buildingsData.map(building => ({
      id: building.id,
      organizationId: building.organization_id,
      name: building.name,
      address: building.address,
      floors: (building.floors || []).map(floor => ({
        id: floor.id,
        number: floor.number,
        apartments: (floor.apartments || []).map(apt => ({
          id: apt.id,
          number: apt.number,
          formattedNumber: apt.formatted_number,
          status: apt.status as any,
          notes: apt.notes || '',
          areaType: apt.area_type as any,
          abortReason: apt.abort_reason,
          technician: apt.technician,
          konzernId: apt.konzern_id,
          lastUpdated: apt.last_updated ? new Date(apt.last_updated) : undefined,
          customFields: apt.custom_fields,
          history: apt.history
        })),
        customFields: floor.custom_fields
      })),
      archivedAt: building.archived_at ? new Date(building.archived_at) : undefined,
      customFields: building.metadata?.customFields,
      metadata: building.metadata
    }));
    
    // Speichere die Gebäude lokal
    saveOfflineBuildings(buildings);
    
    return buildings;
  } catch (error) {
    console.error('Fehler beim Laden der Gebäude aus der Datenbank:', error);
    
    // Fallback auf lokale Daten
    return loadOfflineBuildings();
  }
};

/**
 * Synchronisiert Offline-Änderungen mit der Datenbank
 */
export const syncOfflineChanges = async (): Promise<boolean> => {
  try {
    // Prüfe, ob eine Verbindung besteht
    const isConnected = await checkSupabaseConnection();
    if (!isConnected) {
      throw new Error('Keine Verbindung zur Datenbank');
    }
    
    const changes = loadOfflineChanges();
    const pendingChanges = changes.filter(change => !change.synced);
    
    if (pendingChanges.length === 0) {
      return true;
    }
    
    // Sortiere Änderungen nach Zeitstempel
    pendingChanges.sort((a, b) => a.timestamp - b.timestamp);
    
    // Synchronisiere jede Änderung
    for (const change of pendingChanges) {
      try {
        await syncChange(change);
        
        // Markiere Änderung als synchronisiert
        change.synced = true;
      } catch (error) {
        console.error(`Fehler beim Synchronisieren der Änderung ${change.id}:`, error);
        
        // Erhöhe Anzahl der Synchronisierungsversuche
        change.syncAttempts += 1;
        change.lastSyncAttempt = Date.now();
        change.error = error instanceof Error ? error.message : 'Unbekannter Fehler';
      }
    }
    
    // Speichere aktualisierte Änderungen
    const encryptedData = encryptData(JSON.stringify(changes));
    localStorage.setItem(OFFLINE_CHANGES_KEY, encryptedData);
    
    // Aktualisiere Synchronisierungsstatus
    updateSyncStatus({
      lastSync: Date.now(),
      pendingChanges: getPendingChangesCount(),
      failedChanges: getFailedChangesCount()
    });
    
    // Prüfe, ob alle Änderungen synchronisiert wurden
    return changes.every(change => change.synced);
  } catch (error) {
    console.error('Fehler bei der Synchronisierung:', error);
    
    // Aktualisiere Synchronisierungsstatus
    updateSyncStatus({
      lastError: error instanceof Error ? error.message : 'Unbekannter Fehler'
    });
    
    return false;
  }
};

/**
 * Synchronisiert eine einzelne Änderung mit der Datenbank
 */
const syncChange = async (change: OfflineChange): Promise<void> => {
  switch (change.entityType) {
    case 'building':
      await syncBuildingChange(change);
      break;
    case 'floor':
      await syncFloorChange(change);
      break;
    case 'apartment':
      await syncApartmentChange(change);
      break;
    default:
      throw new Error(`Unbekannter Entitätstyp: ${change.entityType}`);
  }
};

/**
 * Synchronisiert eine Gebäudeänderung mit der Datenbank
 */
const syncBuildingChange = async (change: OfflineChange): Promise<void> => {
  switch (change.type) {
    case 'create':
      await supabase
        .from('buildings')
        .insert({
          id: change.entityId,
          name: change.data.name,
          address: change.data.address,
          organization_id: change.data.organizationId,
          created_by: change.data.createdBy,
          metadata: change.data.metadata
        });
      break;
    case 'update':
      await supabase
        .from('buildings')
        .update({
          name: change.data.name,
          address: change.data.address,
          updated_at: new Date().toISOString(),
          metadata: change.data.metadata
        })
        .eq('id', change.entityId);
      break;
    case 'delete':
      await supabase
        .from('buildings')
        .delete()
        .eq('id', change.entityId);
      break;
  }
};

/**
 * Synchronisiert eine Etagenänderung mit der Datenbank
 */
const syncFloorChange = async (change: OfflineChange): Promise<void> => {
  switch (change.type) {
    case 'create':
      await supabase
        .from('floors')
        .insert({
          id: change.entityId,
          building_id: change.data.buildingId,
          number: change.data.number,
          custom_fields: change.data.customFields
        });
      break;
    case 'update':
      await supabase
        .from('floors')
        .update({
          number: change.data.number,
          custom_fields: change.data.customFields
        })
        .eq('id', change.entityId);
      break;
    case 'delete':
      await supabase
        .from('floors')
        .delete()
        .eq('id', change.entityId);
      break;
  }
};

/**
 * Synchronisiert eine Wohnungsänderung mit der Datenbank
 */
const syncApartmentChange = async (change: OfflineChange): Promise<void> => {
  switch (change.type) {
    case 'create':
      await supabase
        .from('apartments')
        .insert({
          id: change.entityId,
          floor_id: change.data.floorId,
          number: change.data.number,
          formatted_number: change.data.formattedNumber,
          status: change.data.status,
          notes: change.data.notes,
          area_type: change.data.areaType,
          abort_reason: change.data.abortReason,
          technician: change.data.technician,
          konzern_id: change.data.konzernId,
          last_updated: change.data.lastUpdated?.toISOString(),
          custom_fields: change.data.customFields,
          history: change.data.history
        });
      break;
    case 'update':
      await supabase
        .from('apartments')
        .update({
          status: change.data.status,
          notes: change.data.notes,
          area_type: change.data.areaType,
          abort_reason: change.data.abortReason,
          technician: change.data.technician,
          konzern_id: change.data.konzernId,
          last_updated: new Date().toISOString(),
          custom_fields: change.data.customFields,
          history: change.data.history
        })
        .eq('id', change.entityId);
      break;
    case 'delete':
      await supabase
        .from('apartments')
        .delete()
        .eq('id', change.entityId);
      break;
  }
};

/**
 * Prüft, ob eine Verbindung zur Datenbank besteht
 */
const checkSupabaseConnection = async (): Promise<boolean> => {
  try {
    const { data, error } = await supabase.from('buildings').select('count');
    return !error;
  } catch (error) {
    return false;
  }
};

// Export other necessary functions
export { loadOfflineChanges as getOfflineChanges };